import { Directive, Input, OnDestroy } from '@angular/core';
import { CdkDrag } from '@angular/cdk/drag-drop';

/**
 * ドラッグ中に画面端でウィンドウを自動スクロールするディレクティブ
 *
 * 使用方法:
 * <tr mat-row cdkDrag appDragDropWindowScroll [scrollBoundary]="250" [maxScrollSpeed]="100">
 */
@Directive({
  selector: '[appDragDropWindowScroll]'
})
export class DragDropWindowScrollDirective implements OnDestroy {
  @Input() scrollBoundary = 250; // スクロール境界（画面端からの距離）
  @Input() maxScrollSpeed = 400; // 最大スクロール速度
  @Input() minScrollSpeed = 100; // 最小スクロール速度
  @Input() scrollAcceleration = 0.8; // 加速カーブ（0-1、小さいほど急加速）

  private scrollInterval: any;
  private isDragging = false;
  private dragMoveListener: any;
  private dragStartListener: any;
  private dragEndListener: any;

  constructor(private cdkDrag: CdkDrag) {
    this.setupEventListeners();
  }

  ngOnDestroy(): void {
    this.stopWindowScroll();
    this.removeEventListeners();
  }

  private setupEventListeners(): void {
    // CdkDragのイベントをサブスクライブ
    this.dragStartListener = this.cdkDrag.started.subscribe(() => {
      this.onDragStarted();
    });

    this.dragMoveListener = this.cdkDrag.moved.subscribe((event: any) => {
      this.onDragMoved(event);
    });

    this.dragEndListener = this.cdkDrag.ended.subscribe(() => {
      this.onDragEnded();
    });
  }

  private removeEventListeners(): void {
    if (this.dragStartListener) {
      this.dragStartListener.unsubscribe();
    }
    if (this.dragMoveListener) {
      this.dragMoveListener.unsubscribe();
    }
    if (this.dragEndListener) {
      this.dragEndListener.unsubscribe();
    }
  }

  private onDragStarted(): void {
    this.isDragging = true;
  }

  private onDragMoved(event: any): void {
    // ドラッグ中でない場合は何もしない
    if (!this.isDragging) {
      return;
    }

    const pointerPosition = event.pointerPosition;
    const viewportHeight = window.innerHeight;

    // 上端チェック
    if (pointerPosition.y < this.scrollBoundary) {
      // 上にスクロール - 端に近いほど速く
      const distance = this.scrollBoundary - pointerPosition.y;
      const intensity = Math.pow(distance / this.scrollBoundary, this.scrollAcceleration);
      const scrollSpeed = Math.max(this.minScrollSpeed, Math.floor(intensity * this.maxScrollSpeed));
      this.startWindowScroll(-scrollSpeed);
    }
    // 下端チェック
    else if (pointerPosition.y > viewportHeight - this.scrollBoundary) {
      // 下にスクロール - 端に近いほど速く
      const distance = pointerPosition.y - (viewportHeight - this.scrollBoundary);
      const intensity = Math.pow(distance / this.scrollBoundary, this.scrollAcceleration);
      const scrollSpeed = Math.max(this.minScrollSpeed, Math.floor(intensity * this.maxScrollSpeed));
      this.startWindowScroll(scrollSpeed);
    }
    // スクロール領域外
    else {
      this.stopWindowScroll();
    }
  }

  private onDragEnded(): void {
    this.isDragging = false;
    this.stopWindowScroll();
  }

  private startWindowScroll(speed: number): void {
    // 既存のインターバルをクリア
    this.stopWindowScroll();

    this.scrollInterval = setInterval(() => {
      window.scrollBy(0, speed);
    }, 16); // 約60fps
  }

  private stopWindowScroll(): void {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }
}
